import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, ArrowLeft, Loader2 } from "lucide-react";
import AuthLayout from "@/components/AuthLayout";

export default function ForgotPassword() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split("-")[0] || "pt";
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [notRegistered, setNotRegistered] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setNotRegistered(false);
    try {
      await base44.auth.resetPasswordRequest(email.toLowerCase().trim(), lang);
      setSent(true);
    } catch (err) {
      setNotRegistered(err.status === 404);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      icon={Mail}
      title={t("forgotPassword.title")}
      subtitle={t("forgotPassword.subtitle")}
      footer={
        <Link to="/login" data-testid="forgot-login" className="text-primary font-medium hover:underline">
          <ArrowLeft className="w-3 h-3 inline mr-1" />{t("forgotPassword.backToLogin")}
        </Link>
      }
    >
      {sent ? (
        <p data-testid="forgot-sent" role="status" className="text-sm text-foreground text-center">{t("forgotPassword.sentMessage")}</p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div role="alert" className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm" data-testid="forgot-error">
              {error}{" "}
              {notRegistered && <Link data-testid="forgot-register" to="/register" className="underline font-semibold">{t("forgotPassword.registerHere")}</Link>}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">{t("common.email")}</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
              <Input id="email" data-testid="forgot-email-input" type="email" autoComplete="email" autoFocus placeholder="seu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10 h-12" required />
            </div>
          </div>
          <Button type="submit" data-testid="forgot-submit" className="w-full h-12 font-medium" disabled={loading}>
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("forgotPassword.submitting")}</>
            ) : (
              t("forgotPassword.submit")
            )}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
